import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { onAuthStateChanged, signOut } from "firebase/auth"
import { auth } from "../firebase"
import { createCloudSync } from "../utils/cloud-sync"
import { localInventory } from "../utils/local-inventory"

const AuthContext = React.createContext(null)

// "Not now" on the migration prompt snoozes it for 24 hours (stored
// as the dismissal timestamp, so it survives refreshes, tabs, and
// re-logins on this device)
const SNOOZE_KEY = "GA_MIGRATION_SNOOZED_AT"
const SNOOZE_MS = 24 * 60 * 60 * 1000

function migrationSnoozed() {
  const at = parseInt(localStorage.getItem(SNOOZE_KEY), 10)
  return Number.isFinite(at) && Date.now() - at < SNOOZE_MS
}

function hasNonzeroCounts(map) {
  return !!map && Object.values(map).some((count) => count > 0)
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [authReady, setAuthReady] = useState(false)
  const [loginOpen, setLoginOpen] = useState(false)
  const [cloudDoc, setCloudDoc] = useState(null)
  const [cloudLoaded, setCloudLoaded] = useState(false)
  const [migrationNeeded, setMigrationNeeded] = useState(false)
  const syncRef = useRef(null)

  useEffect(() => {
    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser)
      setAuthReady(true)
    })
  }, [])

  useEffect(() => {
    if (!user) {
      return
    }
    const sync = createCloudSync(user.uid)
    syncRef.current = sync
    sync.subscribe((data) => {
      setCloudDoc(data)
      setCloudLoaded(true)
    })
    return () => {
      sync.dispose({ flush: true })
      syncRef.current = null
      setCloudDoc(null)
      setCloudLoaded(false)
      setMigrationNeeded(false)
    }
  }, [user])

  // migration prompt: account has never committed a collection, the
  // account's cloud collection is still empty (an account already
  // built via steppers must never be offered a destructive import),
  // the user hasn't said "never ask", and this device has local counts
  useEffect(() => {
    if (!user || !cloudLoaded || !cloudDoc) {
      return
    }
    if (cloudDoc.collectionImported === true) {
      // consented to in the save/upload dialogs: once a collection is
      // committed to the account, stale device copies are cleared to
      // prevent accidental overwrites
      if (localInventory.load()) {
        localInventory.clear()
      }
      setMigrationNeeded(false)
      return
    }
    if (cloudDoc.migrationPromptDismissed === true) {
      setMigrationNeeded(false)
      return
    }
    if (hasNonzeroCounts(cloudDoc.inventory)) {
      setMigrationNeeded(false)
      return
    }
    if (migrationSnoozed()) {
      setMigrationNeeded(false)
      return
    }
    setMigrationNeeded(hasNonzeroCounts(localInventory.load()))
  }, [user, cloudLoaded, cloudDoc])

  const resolveMigration = useCallback(async (choice) => {
    const sync = syncRef.current
    setMigrationNeeded(false)
    if (!sync) {
      return
    }
    if (choice === "import") {
      const local = localInventory.load()
      try {
        await sync.replaceInventory(local ?? {})
        localInventory.clear()
      } catch (err) {
        console.error("collection import failed", err)
        window.alert(
          "Saving your collection to your account failed, please try again.",
        )
      }
    } else if (choice === "never") {
      await sync.setMigrationDismissed()
    } else {
      // "later": quiet for 24 hours on this device
      localStorage.setItem(SNOOZE_KEY, String(Date.now()))
    }
  }, [])

  const logout = useCallback(async () => {
    if (syncRef.current) {
      // dispose bounds the flush wait internally, so an offline
      // logout proceeds instead of hanging before signOut
      await syncRef.current.dispose({ flush: true })
      syncRef.current = null
    }
    await signOut(auth)
  }, [])

  const openLogin = useCallback(() => setLoginOpen(true), [])
  const closeLogin = useCallback(() => setLoginOpen(false), [])
  const getSync = useCallback(() => syncRef.current, [])

  // memoized so an auth/snapshot change doesn't hand every consumer
  // a fresh object and re-render the whole card grid needlessly
  const value = useMemo(
    () => ({
      user,
      authReady,
      loginOpen,
      openLogin,
      closeLogin,
      logout,
      cloudDoc,
      cloudLoaded,
      getSync,
      migrationNeeded,
      resolveMigration,
    }),
    [
      user,
      authReady,
      loginOpen,
      openLogin,
      closeLogin,
      logout,
      cloudDoc,
      cloudLoaded,
      getSync,
      migrationNeeded,
      resolveMigration,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthContext() {
  return useContext(AuthContext)
}
